import { useMemo } from "react";
import dayjs from "dayjs";
import { removeTimezone } from "halifax";
import {
  FLIGHT_DATES_SESSION_STORAGE_KEY,
  FLIGHT_ROUTE_SESSION_STORAGE_KEY,
} from "../constants";
import type {
  SearchFlightDates,
  SearchFlightRoute,
  ChfarRouteParams,
  ChfarDateParams,
} from "../types";
import { useChfarPolicy } from "./useChfarPolicy";
import { useSessionStorage } from "./useSessionStorage";

const normalizeFlightDates = (date: string) =>
  dayjs(removeTimezone(date)).toDate();

/**
 * This hook is used to get the parameters used for the ChFAR flight search.
 * The parameters are derived from the stored search options in session storage or from the original ChFAR policy.
 * @param itineraryId
 * @param contractId
 */
export const useChfarSearchedFlight = (
  itineraryId: string,
  contractId: string
) => {
  const [storedFlightDatesRecord] = useSessionStorage<
    Record<string, SearchFlightDates>
  >(FLIGHT_DATES_SESSION_STORAGE_KEY, {});
  const [storedFlightRouteRecord] = useSessionStorage<
    Record<string, SearchFlightRoute>
  >(FLIGHT_ROUTE_SESSION_STORAGE_KEY, {});

  const { data: chfarPolicy } = useChfarPolicy(
    { itineraryId: itineraryId as string, contractId: contractId as string },
    !itineraryId || !contractId
  );

  const storedFlightDates = useMemo(
    () => storedFlightDatesRecord?.[itineraryId],
    [storedFlightDatesRecord, itineraryId]
  );

  const normalizedFlightDates = useMemo(() => {
    if (chfarPolicy == null) {
      return chfarPolicy;
    }
    return {
      departureDate: normalizeFlightDates(
        chfarPolicy.flightDates.departureDate
      ),
      ...(chfarPolicy.flightDates.returnDate && {
        returnDate: normalizeFlightDates(chfarPolicy.flightDates.returnDate),
      }),
    };
  }, [chfarPolicy]);

  const searchDates = storedFlightDates ?? normalizedFlightDates ?? {};

  const searchDateParams = Object.keys(searchDates).reduce((acc, key) => {
    acc[key] = dayjs(searchDates[key]).format("YYYY-MM-DD");
    return acc;
  }, {} as ChfarDateParams);

  const storedFlightRoute = useMemo(
    () => storedFlightRouteRecord?.[itineraryId],
    [storedFlightRouteRecord, itineraryId]
  );

  const chfarPolicyRouteParams = {
    origin: chfarPolicy?.flightRoute.origin.code,
    originRegionType: chfarPolicy?.flightRoute.origin.regionType,
    destination: chfarPolicy?.flightRoute.destination.code,
    destinationRegionType: chfarPolicy?.flightRoute.destination.regionType,
  };

  const searchRouteParams: ChfarRouteParams = storedFlightRoute
    ? Object.keys(storedFlightRoute).reduce((acc, key) => {
        const slice = storedFlightRoute[key as keyof typeof storedFlightRoute];
        if (slice.id.Id === "Flight") {
          acc[key] = slice.id.code.code;
          acc[`${key}RegionType`] = slice.id.code.regionType;
        }
        return acc;
      }, {})
    : chfarPolicyRouteParams;

  return useMemo(
    () => ({
      searchDateParams,
      searchRouteParams,
    }),
    [searchDateParams, searchRouteParams]
  );
};
